import React, { useEffect, useMemo, useState } from 'react';
import styles from './schedule-item.module.scss';
import { ReactComponent as Trophy } from '../../../static/icon-trophy.svg';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment-timezone';
import { getTeamConfig, getEnvConfig } from '../../../config/config';

interface ScheduleItemProps {
  data?: any;
  type?: string;
}

const ScheduleItem: React.FC<ScheduleItemProps> = ({ data, type }) => {
  const [teamLogoLoaded, setTeamLogoLoaded] = useState(false);

  const location = () => {
    if (data?.contextualInfo.homeTeam === 'THIS_TEAM') {
      return (
        <abbr title="Versus" className={`${styles.label} ${styles.vs}`}>
          Vs
        </abbr>
      );
    }
    if (data?.contextualInfo.homeTeam === 'OPPONENT') {
      return <span className={`${styles.label} ${styles.at}`}>At</span>;
    }
    return '';
  };

  const teamUrl = getTeamConfig().teamUrl;

  const scorecardLink = 'scorecard/' + data.id.toUpperCase();

  const loaded = () => {
    setTeamLogoLoaded(true);
  };

  const siteUrl = /\/$/.test(getEnvConfig().site) ? getEnvConfig().site : `${getEnvConfig().site}/`;
  let opponentLogo = useMemo(() => new Image(), []);
  opponentLogo.src = `${siteUrl}${data?.contextualInfo.opponent.id}/Logo`;

  useEffect(() => {
    opponentLogo.addEventListener('load', loaded);

    return () => {
      opponentLogo.removeEventListener('load', loaded);
    };
  }, [opponentLogo]);

  const timezone = (sDT: any) => {
    /* Guess the timezone */
    let thisTz = moment.tz.guess();

    if (sDT?.timezoneName !== '' && sDT?.timezoneName !== null) {
      thisTz = sDT.timezoneName;
    }

    return thisTz;
  };

  let linkNames: any = {
    videoLink: 'Video',
    scoreLink: 'Live score'
  };

  const liveLinks = () => {
    if (data.webLinks && data.webLinks.length > 0) {
      let webLinks = data.webLinks.map((obj: any, index: number) => (
        <a
          data-testid="PT-scheduleItem-liveScoreLink"
          href={obj.url}
          key={index}
          className={styles.boxLink}
          target="_blank"
          rel="noreferrer"
        >
          {linkNames[obj.name]}
        </a>
      ));
      return webLinks;
    }

    return <></>;
  };

  return (
    <div className={styles.item}>
      <div
        className={`${styles.opponent} ${
          data?.isConferenceMatch === false ? styles.noConference : ''
        }`}
      >
        {teamLogoLoaded && (
          <div className={styles.logo}>
            <a
              data-testid="PT-scheduleItem-logoTeamLink"
              href={teamUrl + data?.contextualInfo.opponent.id + '&a=Index&c=VenueTeam'}
            >
              <img src={opponentLogo.src} alt={data?.contextualInfo.opponent.name} />
            </a>
          </div>
        )}
        <div className={styles.timeTeam}>
          <time>
            {data?.startDateTime.noScheduledTime === true
              ? moment(data?.startDateTime.dateTimeString).format('MMM D (ddd), YYYY / TBC')
              : moment(data?.startDateTime.dateTimeString)
                  .tz(timezone(data.startDateTime))
                  .format('MMM D (ddd), YYYY / h:mm A z')}
          </time>
          <p>
            {location()}{' '}
            <a
              data-testid="PT-scheduleItem-teamLink"
              href={teamUrl + data?.contextualInfo.opponent.id + '&a=Index&c=VenueTeam'}
            >
              {data?.contextualInfo.opponent.name}
            </a>
          </p>
        </div>
      </div>
      <div className={styles.location}>
        <p>
          {data?.isConferenceMatch === true && (
            <abbr title="Conference" className={`${styles.label} ${styles.conf}`}>
              Conf
            </abbr>
          )}
          {data?.location}
        </p>
      </div>
      {data?.contextualInfo.didWin !== null && (
        <div
          className={`${styles.result} ${
            data?.contextualInfo.didWin === false ? styles.didNotWin : ''
          }`}
        >
          <div className={styles.didWin}>{data?.contextualInfo.didWin === true && <Trophy />}</div>
          <div className={styles.score}>
            <p className={styles.score}>
              {data?.contextualInfo.didWin === true ? 'W' : 'L'}, {data?.contextualInfo.result}
            </p>
            <Link to={scorecardLink}>Box score</Link>
          </div>
        </div>
      )}

      {data?.contextualInfo.didWin === null && (
        <div className={styles.resultEmpty}>
          {type === 'upcoming' && <div className={styles.links}>{liveLinks()}</div>}
        </div>
      )}
    </div>
  );
};

export default ScheduleItem;
